/** @jsx isolateComponent */
import { isolateComponent } from '@zapier/style-encapsulation';
import {
  Colors,
  Heading,
  HeadingLevel,
  Link,
  Spacer,
  Typography,
} from '@zapier/design-system';

import Surface from '@zapier/product-get-help/src/components/shared/Surface';
import HelpCenterLinkIcon from '@zapier/product-get-help/src/components/shared/HelpCenterLinkIcon';

import PlayIcon from './icons/play.svg';
import InvoiceIcon from './icons/invoice.svg';
import IdeaIcon from './icons/idea.svg';

type PositiveFeedbackProps = {
  /** Called when you click 'see more solutions'. */
  onGoToNextStep: () => void;
  /** Called when you click a link in the positive feedback "Help Center" box. */
  onClickHelpCenterLink: (href: string) => void;
};

const Styles = {
  positiveFeedbackTitle: {
    ...Typography.sectionHeader1,
    color: Colors.gray,
  },
  positiveFeedbackText: {
    ...Typography.paragraph3,
    color: Colors.gray80,
  },
  helpLink: {
    display: 'grid',
    columnGap: 10,
    gridTemplateColumns: '40px 1fr',
    '&:hover, &:hover .helpLinkTitle': {
      color: Colors.ocean,
    },
  },
  helpLinkTitle: {
    ...Typography.paragraph3Bold,
    color: Colors.gray,
  },
  helpLinkText: {
    ...Typography.smallPrint1,
  },
  root: {
    display: 'grid',
    gridGap: 10,
  },
  surface: {
    display: 'grid',
    gridGap: 25,
  },
};

const RebrandStyles = {
  positiveFeedbackTitle: {
    ...Typography.pageHeader7,
    color: Colors.neutral800,
  },
  positiveFeedbackText: {
    ...Typography.paragraph3,
    color: Colors.neutral800,
  },
  helpLink: {
    display: 'grid',
    columnGap: 10,
    gridTemplateColumns: '40px 1fr',
    color: Colors.neutral700,
    '&:hover, &:hover .helpLinkTitle': {
      color: Colors.blueJeans,
    },
  },
  helpLinkTitle: {
    ...Typography.paragraph3Bold,
    color: Colors.neutral800,
  },
  helpLinkText: {
    ...Typography.smallPrint1,
  },
};

const HELP_CENTER_LINKS = [
  {
    href: 'https://zapier.com/learn/university/',
    icon: <PlayIcon />,
    rebrandIcon: (
      <HelpCenterLinkIcon color="neutral800" name="miscLearn" size={40} />
    ),
    title: 'Zapier University',
    text: 'Instructional video courses from the Zapier team',
  },
  {
    href: 'https://zapier.com/resources/guides',
    icon: <InvoiceIcon />,
    rebrandIcon: (
      <HelpCenterLinkIcon color="neutral800" name="actionBookmark" size={40} />
    ),
    title: 'The Learning Center',
    text: 'Educational resources to make your work easier',
  },
  {
    href: 'https://zapier.com/blog/',
    icon: <IdeaIcon />,
    rebrandIcon: (
      <HelpCenterLinkIcon color="neutral800" name="navBlog" size={40} />
    ),
    title: 'Zapier Blog',
    text: 'Tips and advice for automation across the board',
  },
];

/**
 * Showed when you click 'Yes, thank you' in the `<Feedback>` component.
 */
const PositiveFeedback = ({
  onGoToNextStep,
  onClickHelpCenterLink,
}: PositiveFeedbackProps) => {
  const onClickMore: React.MouseEventHandler = e => {
    e.preventDefault();
    onGoToNextStep();
  };

  const onClickLink: React.MouseEventHandler = e => {
    const href =
      e.currentTarget.getAttribute('href') || 'https://zapier.com/app/get-help';
    e.preventDefault();
    onClickHelpCenterLink(href);
  };

  return (
    <div css={Styles.root}>
      <Heading css={RebrandStyles.positiveFeedbackTitle}>
        We’re glad that helped
      </Heading>
      <HeadingLevel>
        <p css={RebrandStyles.positiveFeedbackText}>
          Thanks for providing feedback, we’re glad this could help you! You can
          still{' '}
          <Link href="#" onClick={onClickMore}>
            see more solutions
          </Link>{' '}
          if you’d like to or have further questions.
        </p>
        <Spacer height={16} />
        <Surface
          hasShadow={false}
          title="Help Center"
          titleTypography="subHeader1Bold"
        >
          <div css={Styles.surface}>
            <p>
              From definitions to tutorials, find what you need to get your Zaps
              running.
            </p>
            {HELP_CENTER_LINKS.map(({ href, rebrandIcon, title, text }) => (
              <Link
                css={RebrandStyles.helpLink}
                href={href}
                key={href}
                onClick={onClickLink}
              >
                {rebrandIcon}
                <div>
                  {/* Add custom className so that we can cascade override the color on hover */}
                  <Heading className="helpLinkTitle" css={Styles.helpLinkTitle}>
                    {title}
                  </Heading>
                  <p css={RebrandStyles.helpLinkText}>{text}</p>
                </div>
              </Link>
            ))}
          </div>
        </Surface>
      </HeadingLevel>
    </div>
  );
};

export default PositiveFeedback;
